import { c as _c } from "react/compiler-runtime";
import { controlVisibility } from '#components/chart/utils/controlVisibility';
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebounceCallback } from 'usehooks-ts';
export default function useOnScreen(ref) {
  const $ = _c(6);
  const observerRef = useRef(null);
  const [isOnScreen, setIsOnScreen] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      observerRef.current = new IntersectionObserver(t2 => {
        const [entry] = t2;
        return setIsOnScreen(entry.isIntersecting);
      }, {
        rootMargin: "1000px"
      });
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] !== ref.current) {
    t2 = () => {
      if (!ref.current || !observerRef.current) {
        return;
      }
      const observer = observerRef.current;
      observer.observe(ref.current);
      return () => {
        observer.disconnect();
      };
    };
    $[2] = ref.current;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== ref) {
    t3 = [ref];
    $[4] = ref;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useEffect(t2, t3);
  return isOnScreen;
}
type ChartElement = SVGElement | HTMLElement;
interface UseChartObserverOptions {
  ticksContainerClassName?: string;
  labelsContainerClassName?: string;
}
const DEFAULT_OPTIONS = {
  ticksContainerClassName: 'recharts-cartesian-axis-ticks',
  labelsContainerClassName: 'recharts-label-list'
} as const;
export function useChartObserver(options?: UseChartObserverOptions) {
  const mergedOptions = useMemo(() => ({
    ...DEFAULT_OPTIONS,
    ...options
  }), [options]);
  const {
    ticksContainerClassName,
    labelsContainerClassName
  } = mergedOptions;
  const [isRendered, setIsRendered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const resizeObserver = useRef<ResizeObserver>(null);
  const [isCalculated, setIsCalculated] = useState(false);
  const recalcVisibility = useCallback(() => {
    if (!containerRef.current) return;
    try {
      const ticksContainer = containerRef.current.getElementsByClassName(ticksContainerClassName)[0] as SVGElement | undefined;
      const labelsContainers = containerRef.current.getElementsByClassName(labelsContainerClassName);
      const elements = getAllChartElements(ticksContainer ?? null, labelsContainers);
      if (elements.length > 0) {
        controlVisibility(elements);
        setIsCalculated(true);
      }
    } catch (error) {
      console.error('Error controlling chart element visibility:', error);
    }
  }, [ticksContainerClassName, labelsContainerClassName]);
  const isIntersecting = useOnScreen(containerRef);
  const isVisible = isIntersecting || isCalculated;
  const debouncedRecalcVisibility = useDebounceCallback(recalcVisibility, 100);
  useEffect(() => {
    setIsRendered(true);
    resizeObserver.current = new ResizeObserver(() => {
      recalcVisibility();
      debouncedRecalcVisibility();
    });
  }, [debouncedRecalcVisibility, recalcVisibility]);
  useEffect(() => {
    if (!isRendered || !containerRef.current || !isVisible || !resizeObserver.current) return;
    const observer = resizeObserver.current;
    observer.observe(containerRef.current);
    return () => {
      observer.disconnect();
    };
  }, [isRendered, containerRef, recalcVisibility, debouncedRecalcVisibility, isVisible]);
  return {
    containerRef,
    isRendered,
    isVisible,
    isCalculated
  };
}
function getAllChartElements(ticksContainer: ChartElement | null, labelsContainers: HTMLCollectionOf<Element>): SVGTextElement[] {
  const ticksElements = ticksContainer ? Array.from(ticksContainer.children).flatMap(child => getTextElements(child.children)) : [];
  const labelElements = Array.from(labelsContainers).flatMap(container => getTextElements(container.children));
  return [...ticksElements, ...labelElements];
}
function getTextElements(elements: HTMLCollection | undefined): SVGTextElement[] {
  if (!elements) return [];
  return Array.from(elements).filter((element): element is SVGTextElement => element.tagName.toLowerCase() === 'text');
}